import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CalendarIcon,
  CalendarDaysIcon,
  MapPinIcon,
  UserGroupIcon,
  VideoCameraIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ClockIcon,
  CurrencyEuroIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, Avatar, InlineSpinner, EmptyState, Pagination } from '@/components/ui'
import { eventsApi } from '@/api'
import type { Event } from '@/api/events'
import EventCalendar from '@/components/events/EventCalendar'

export default function EventsPage() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [selectedType, setSelectedType] = useState<string>('')
  const [modeFilters, setModeFilters] = useState<{ online: boolean; in_presenza: boolean; ibrido: boolean }>({
    online: true,
    in_presenza: true,
    ibrido: true,
  })
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'calendar'>('grid')
  const [showPast, setShowPast] = useState(false)

  // Event types
  const eventTypes = [
    { value: 'webinar', label: 'Webinar', icon: VideoCameraIcon },
    { value: 'workshop', label: 'Workshop', icon: UserGroupIcon },
    { value: 'conference', label: 'Conferenza', icon: BuildingOfficeIcon },
    { value: 'community_day', label: 'Community Day', icon: UserGroupIcon },
    { value: 'training', label: 'Training', icon: CalendarIcon },
    { value: 'meeting', label: 'Riunione', icon: UserGroupIcon },
    { value: 'other', label: 'Altro', icon: CalendarIcon },
  ]

  useEffect(() => {
    fetchEvents()
  }, [currentPage, selectedType, modeFilters, showPast])

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const data = await eventsApi.getEvents({
        page: currentPage,
        type: selectedType || undefined,
        // When "show past" is checked, show ALL events; otherwise only upcoming
        upcoming: showPast ? undefined : true,
      })

      // Filter by mode client-side (since backend doesn't support multiple modes)
      let filteredEvents = data.results || []
      const activeModes = Object.entries(modeFilters)
        .filter(([_, isActive]) => isActive)
        .map(([mode]) => mode)

      // Only filter if not all modes are selected
      if (activeModes.length < 3) {
        filteredEvents = filteredEvents.filter(event =>
          activeModes.includes(event.mode)
        )
      }

      setEvents(filteredEvents)
      setTotalPages(Math.ceil((data.count || 0) / 12))
      setTotalItems(data.count || 0)
    } catch (error) {
      console.error('Error fetching events:', error)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTypeBadge = (type: string) => {
    // Map backend event types to display config
    const config: Record<string, { variant: 'info' | 'success' | 'warning' | 'default', label: string, icon: typeof VideoCameraIcon }> = {
      // Backend event types
      webinar: { variant: 'info', label: 'Webinar', icon: VideoCameraIcon },
      workshop: { variant: 'success', label: 'Workshop', icon: UserGroupIcon },
      conference: { variant: 'warning', label: 'Conferenza', icon: BuildingOfficeIcon },
      community_day: { variant: 'success', label: 'Community Day', icon: UserGroupIcon },
      training: { variant: 'info', label: 'Formazione', icon: CalendarIcon },
      meeting: { variant: 'default', label: 'Riunione', icon: UserGroupIcon },
      meetup: { variant: 'success', label: 'Meetup', icon: UserGroupIcon },
      other: { variant: 'default', label: 'Altro', icon: CalendarIcon },
    }
    // Fallback for unknown types
    return config[type] || { variant: 'default' as const, label: type || 'Evento', icon: CalendarIcon }
  }

  const getModeBadge = (mode: string) => {
    const config: Record<string, { variant: 'info' | 'success' | 'warning' | 'default', label: string, icon: typeof VideoCameraIcon }> = {
      online: { variant: 'info', label: 'Online', icon: VideoCameraIcon },
      in_presenza: { variant: 'success', label: 'In presenza', icon: BuildingOfficeIcon },
      ibrido: { variant: 'warning', label: 'Ibrido', icon: MapPinIcon },
    }
    return config[mode] || { variant: 'default' as const, label: mode || 'N/D', icon: CalendarIcon }
  }

  const isEventPast = (endDate: string) => {
    return new Date(endDate) < new Date()
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento eventi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Eventi</h1>
          <p className="text-gray-600">Scopri gli eventi della community e partecipa</p>
        </div>
        <Link
          to="/events/my-events"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <CalendarIcon className="h-5 w-5 mr-2" />
          I miei eventi
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          {/* Row 1: Type Filter */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Tipo:</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  setSelectedType('')
                  setCurrentPage(1)
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  !selectedType
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tutti
              </button>
              {eventTypes.map((type) => {
                const TypeIcon = type.icon
                return (
                  <button
                    key={type.value}
                    onClick={() => {
                      setSelectedType(type.value)
                      setCurrentPage(1)
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                      selectedType === type.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <TypeIcon className="h-4 w-4" />
                    {type.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Row 2: Mode Filters (switches) + Past Events + View Mode */}
          <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100">
            <span className="text-sm font-medium text-gray-700">Modalità:</span>

            {/* Online Switch */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={modeFilters.online}
                  onChange={(e) => {
                    setModeFilters(prev => ({ ...prev, online: e.target.checked }))
                    setCurrentPage(1)
                  }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4"></div>
              </div>
              <span className="text-sm text-gray-700 flex items-center gap-1">
                <VideoCameraIcon className="h-4 w-4" />
                Online
              </span>
            </label>

            {/* In Presenza Switch */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={modeFilters.in_presenza}
                  onChange={(e) => {
                    setModeFilters(prev => ({ ...prev, in_presenza: e.target.checked }))
                    setCurrentPage(1)
                  }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-green-500 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4"></div>
              </div>
              <span className="text-sm text-gray-700 flex items-center gap-1">
                <BuildingOfficeIcon className="h-4 w-4" />
                In presenza
              </span>
            </label>

            {/* Ibrido Switch */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={modeFilters.ibrido}
                  onChange={(e) => {
                    setModeFilters(prev => ({ ...prev, ibrido: e.target.checked }))
                    setCurrentPage(1)
                  }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-amber-500 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4"></div>
              </div>
              <span className="text-sm text-gray-700 flex items-center gap-1">
                <MapPinIcon className="h-4 w-4" />
                Ibrido
              </span>
            </label>

            <div className="flex-1" />

            {/* Past Events Toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showPast}
                onChange={(e) => {
                  setShowPast(e.target.checked)
                  setCurrentPage(1)
                }}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Mostra eventi passati
            </label>

            {/* View Mode */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-500'}`}
                title="Vista griglia"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-500'}`}
                title="Vista lista"
              >
                <ListBulletIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 ${viewMode === 'calendar' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-500'}`}
                title="Vista calendario"
              >
                <CalendarDaysIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Calendar View */}
      {viewMode === 'calendar' ? (
        <EventCalendar />
      ) : (
        /* Grid/List Events */
        events.length > 0 ? (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => {
                const typeConfig = getTypeBadge(event.type)
                const TypeIcon = typeConfig.icon
                const modeConfig = getModeBadge(event.mode)
                const ModeIcon = modeConfig.icon
                const isPast = isEventPast(event.end_datetime)

                return (
                  <Link key={event.id} to={`/events/${event.slug}`} className="group">
                    <Card className={`h-full hover:shadow-lg transition-shadow ${isPast ? 'opacity-70' : ''}`}>
                      {/* Thumbnail */}
                      <div className="relative h-40 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gradient-to-br from-primary-500 to-primary-700">
                        {event.image ? (
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <CalendarIcon className="h-16 w-16 text-white/30" />
                          </div>
                        )}
                        {/* Type Badge (top-right) */}
                        <div className="absolute top-2 right-2">
                          <Badge variant={typeConfig.variant}>
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                        </div>
                        {/* Mode Badge (top-left) */}
                        <div className="absolute top-2 left-2">
                          <Badge variant={modeConfig.variant}>
                            <ModeIcon className="h-3 w-3 mr-1" />
                            {modeConfig.label}
                          </Badge>
                        </div>
                        {isPast && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <Badge variant="default" size="lg">Concluso</Badge>
                          </div>
                        )}
                        {event.is_registered && !isPast && (
                          <div className="absolute bottom-2 left-2">
                            <Badge variant="success">Iscritto</Badge>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2 flex-1">
                            {event.title}
                          </h3>
                          {/* Free/Paid Badge */}
                          {event.is_free ? (
                            <Badge variant="success" size="sm">
                              <CheckBadgeIcon className="h-3 w-3 mr-1" />
                              Gratuito
                            </Badge>
                          ) : (
                            <Badge variant="warning" size="sm">
                              <CurrencyEuroIcon className="h-3 w-3 mr-1" />
                              A pagamento
                            </Badge>
                          )}
                        </div>

                        {event.short_description && (
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {event.short_description}
                          </p>
                        )}

                        <div className="space-y-2 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            <span>{formatDate(event.start_datetime)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ClockIcon className="h-4 w-4" />
                            <span>{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2">
                              <MapPinIcon className="h-4 w-4" />
                              <span className="truncate">{event.location}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <UserGroupIcon className="h-4 w-4" />
                            <span>
                              {event.registrations_count}
                              {event.max_participants && ` / ${event.max_participants}`}
                            </span>
                          </div>
                          {event.organizer_name && (
                            <Avatar
                              size="sm"
                              name={event.organizer_name}
                            />
                          )}
                        </div>
                      </div>
                    </Card>
                  </Link>
                )
              })}
            </div>
          ) : (
            <Card>
              <div className="divide-y divide-gray-100">
                {events.map((event) => {
                  const typeConfig = getTypeBadge(event.type)
                  const TypeIcon = typeConfig.icon
                  const modeConfig = getModeBadge(event.mode)
                  const ModeIcon = modeConfig.icon
                  const isPast = isEventPast(event.end_datetime)

                  return (
                    <Link
                      key={event.id}
                      to={`/events/${event.slug}`}
                      className={`flex items-center gap-4 py-4 px-2 -mx-2 hover:bg-gray-50 rounded-lg transition-colors ${isPast ? 'opacity-70' : ''}`}
                    >
                      {/* Date Block */}
                      <div className="flex-shrink-0 w-16 text-center">
                        <div className="text-sm font-medium text-primary-600">
                          {new Date(event.start_datetime).toLocaleDateString('it-IT', { month: 'short' }).toUpperCase()}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">
                          {new Date(event.start_datetime).getDate()}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 truncate">{event.title}</h3>
                          <Badge variant={typeConfig.variant} size="sm">
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                          <Badge variant={modeConfig.variant} size="sm">
                            <ModeIcon className="h-3 w-3 mr-1" />
                            {modeConfig.label}
                          </Badge>
                          {event.is_free ? (
                            <Badge variant="success" size="sm">
                              <CheckBadgeIcon className="h-3 w-3 mr-1" />
                              Gratuito
                            </Badge>
                          ) : (
                            <Badge variant="warning" size="sm">
                              <CurrencyEuroIcon className="h-3 w-3 mr-1" />
                              A pagamento
                            </Badge>
                          )}
                          {event.is_registered && <Badge variant="success" size="sm">Iscritto</Badge>}
                          {isPast && <Badge variant="default" size="sm">Concluso</Badge>}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-4 w-4" />
                            {formatTime(event.start_datetime)}
                          </span>
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPinIcon className="h-4 w-4" />
                              {event.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <UserGroupIcon className="h-4 w-4" />
                            {event.registrations_count} iscritti
                          </span>
                        </div>
                      </div>

                      {/* Organizer */}
                      {event.organizer_name && (
                        <div className="flex items-center gap-2">
                          <Avatar
                            size="sm"
                            name={event.organizer_name}
                          />
                          <span className="text-sm text-gray-500 hidden lg:block">
                            {event.organizer_name}
                          </span>
                        </div>
                      )}
                    </Link>
                  )
                })}
              </div>
            </Card>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={12}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<CalendarIcon className="h-12 w-12" />}
            title="Nessun evento"
            description={showPast ? "Non ci sono eventi passati" : "Non ci sono eventi in programma"}
          />
        </Card>
      )
      )}
    </div>
  )
}
